import { type ReactNode, useEffect, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EmbeddedDocsRouter } from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { techdocsRdapVersionStoreApiRef } from '../apis/versionStore';
import { RdapTechDocsToolbar } from './RdapTechDocsToolbar';

export interface RdapTechDocsPageProps {
  addons: ReactNode[];
}

// The stock reader page wraps its content in @backstage/core-components'
// <Content>, which always adds its own top padding — that's the gap between
// our toolbar and the generated docs' tab bar below it. Content has no prop
// to disable just the top padding, so it's zeroed out from here instead.
const useStyles = makeStyles(() => ({
  content: {
    '& article[class*="BackstageContent-root"]': {
      paddingTop: 0,
    },
  },
}));

/**
 * The stock TechDocs reader page has no notion of "version" anywhere in its
 * fetch hooks, so the only way to make it re-fetch for a newly selected
 * branch/tag is to remount it — this wrapper keys `EmbeddedDocsRouter` off
 * the version picker's current selection for that purpose.
 */
export const RdapTechDocsPage = ({ addons }: RdapTechDocsPageProps) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const versionStore = useApi(techdocsRdapVersionStoreApiRef);

  const entityId = {
    kind: entity.kind,
    namespace: entity.metadata.namespace ?? 'default',
    name: entity.metadata.name,
  };

  const [selectedRef, setSelectedRef] = useState(versionStore.get(entityId));

  useEffect(
    () =>
      versionStore.subscribe(() => setSelectedRef(versionStore.get(entityId))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [versionStore, entityId.kind, entityId.namespace, entityId.name],
  );

  return (
    <>
      <RdapTechDocsToolbar />
      <div className={classes.content}>
        <EmbeddedDocsRouter key={selectedRef ?? 'default'} withSearch={false}>
          <TechDocsAddons>{addons}</TechDocsAddons>
        </EmbeddedDocsRouter>
      </div>
    </>
  );
};
